// FAB (First Abu Dhabi Bank) — Cashback Visa.
// Single-row transactions. Posting date and description are concatenated in one
// cell at x≈89: "DD-MM-YYYY DESCRIPTION". Date format is DD-MM-YYYY (dash).
// Columns: trxnDate@41 | postingDate+desc@89 | [city@252] | [country@320] | currency@341
//          | [origAmount@382] | debit@~460 | credit@~530
// Summary: statement/due dates on a shared row near y696; current balance + min
// payment on the card-number row near y654; credit/available limits near y552.
import { toNum, amountWithSign } from '../util.js';

// "11-05-2026" → YYYY-MM-DD
const dmyDash = (s) => {
  const m = String(s).match(/(\d{1,2})-(\d{1,2})-(\d{2,4})/);
  if (!m) return null;
  let [, d, mo, y] = m;
  if (y.length === 2) y = '20' + y;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

export function parseFAB(pages) {
  const p1 = pages[0] || [];
  const byY1 = {};
  for (const it of p1) { const k = Math.round(it.y / 3) * 3; (byY1[k] = byY1[k] || []).push(it); }

  const summary = {
    statementDate: null, dueDate: null,
    minimumPayment: null, creditLimit: null, availableLimit: null, closingBalance: null,
    previousBalance: null, totalPaymentDue: null,
  };

  // "Summary Details" row: Previous Balance | Purchases | Cash | Finance |
  // Payments | Total Payment Due (values sit just below the labels). The cycle's
  // activity reconciles to Total Payment Due, NOT the card-row Current Balance
  // (which also carries the unbilled installment outstanding).
  const sumLabelY = Object.keys(byY1).map(Number).find(y => (byY1[y] || []).some(it => it.s.includes('Previous Balance')));
  if (sumLabelY != null) {
    for (const y of Object.keys(byY1).map(Number).filter(y => y < sumLabelY && sumLabelY - y <= 25).sort((a, b) => b - a)) {
      const cells = (byY1[y] || []).sort((a, b) => a.x - b.x);
      const prev = cells.find(c => c.x >= 60 && c.x <= 95 && /^[\d,]+\.\d{2}$/.test(c.s));
      const tpd = cells.find(c => c.x >= 495 && c.x <= 520 && /^[\d,]+\.\d{2}(CR)?$/.test(c.s));
      if (prev && tpd) {
        summary.previousBalance = toNum(prev.s);
        const { amount, credit } = amountWithSign(tpd.s);
        summary.totalPaymentDue = credit ? -amount : amount;
        break;
      }
    }
  }

  // Statement date @x≈401 + due date @x≈495 — same y-band
  for (const y of Object.keys(byY1).map(Number).sort((a, b) => b - a)) {
    const cells = byY1[y].sort((a, b) => a.x - b.x);
    const sd = cells.find(c => c.x >= 392 && c.x <= 415 && /^\d{2}-\d{2}-\d{4}$/.test(c.s));
    const dd = cells.find(c => c.x >= 486 && c.x <= 510 && /^\d{2}-\d{2}-\d{4}$/.test(c.s));
    if (sd && dd) { summary.statementDate = dmyDash(sd.s); summary.dueDate = dmyDash(dd.s); break; }
  }

  // Card number row: current balance @x≈415, minimum payment @x≈512
  for (const y of Object.keys(byY1).map(Number).sort((a, b) => b - a)) {
    const cells = byY1[y].sort((a, b) => a.x - b.x);
    if (!cells.find(c => /\d{4}\s+\d{2}\*\*/.test(c.s))) continue;
    const bal = cells.find(c => c.x >= 405 && c.x <= 430 && /^[\d,]+\.\d{2}$/.test(c.s));
    const min = cells.find(c => c.x >= 500 && c.x <= 525 && /^[\d,]+\.\d{2}$/.test(c.s));
    if (bal) summary.closingBalance = toNum(bal.s);
    if (min) summary.minimumPayment = toNum(min.s);
    break;
  }

  // Credit limits: anchor on the "Total Credit Limit" label row, read values in the band below
  const limitLabelY = Object.keys(byY1).map(Number)
    .find(y => (byY1[y] || []).some(it => it.s.includes('Total Credit Limit')));
  if (limitLabelY != null) {
    const below = Object.keys(byY1).map(Number)
      .filter(y => y < limitLabelY && limitLabelY - y <= 30)
      .sort((a, b) => b - a);
    for (const y of below) {
      const cells = byY1[y].sort((a, b) => a.x - b.x);
      const n1 = cells.find(c => c.x >= 60 && c.x <= 85  && /^[\d,]+\.\d{2}$/.test(c.s));
      const n2 = cells.find(c => c.x >= 130 && c.x <= 160 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (n1 && n2) { summary.creditLimit = toNum(n1.s); summary.availableLimit = toNum(n2.s); break; }
    }
  }

  // FAB Rewards points balance: anchor on "Rewards balance" header (x≈170),
  // value (integer points) sits ~33 pts below at x≈175–215.
  summary.rewardsPoints = null;
  const rewardsLabelY = Object.keys(byY1).map(Number)
    .find(y => (byY1[y] || []).some(it => it.s === 'Rewards balance'));
  if (rewardsLabelY != null) {
    const below = Object.keys(byY1).map(Number)
      .filter(y => y < rewardsLabelY && rewardsLabelY - y <= 40)
      .sort((a, b) => b - a);
    for (const y of below) {
      const cells = (byY1[y] || []).sort((a, b) => a.x - b.x);
      const pts = cells.find(c => c.x >= 175 && c.x <= 215 && /^[\d,]+$/.test(c.s));
      if (pts) { summary.rewardsPoints = toNum(pts.s); break; }
    }
  }

  const txns = [];
  for (const page of pages) {
    const byY = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
    for (const y of Object.keys(byY).map(Number).sort((a, b) => b - a)) {
      const cells = byY[y].sort((a, b) => a.x - b.x);

      // Transaction date at x≈41. Page 1 puts a SINGLE date here with the posting
      // date+description merged at x≈89. The later-page continuation instead merges
      // BOTH dates into this cell ("DD-MM-YYYY DD-MM-YYYY") with the description in
      // a separate cell at x≈134. Handle both, or page-2/3 rows silently drop.
      const trxnCell = cells.find(c => c.x >= 36 && c.x <= 52 && /^\d{2}-\d{2}-\d{4}/.test(c.s));
      if (!trxnCell) continue;
      const trxnDate = dmyDash(trxnCell.s.slice(0, 10));

      let postingDate, desc;
      const bothDates = trxnCell.s.match(/^\d{2}-\d{2}-\d{4}\s+(\d{2}-\d{2}-\d{4})\s*$/);
      if (bothDates) {
        postingDate = dmyDash(bothDates[1]);
        const descCell = cells.find(c => c.x >= 120 && c.x <= 320 && /[A-Za-z]/.test(c.s) && !/^[A-Z]{3}$/.test(c.s));
        desc = descCell ? descCell.s.trim() : '';
      } else {
        const postDescCell = cells.find(c => c.x >= 84 && c.x <= 100 && /^\d{2}-\d{2}-\d{4}\s/.test(c.s));
        if (!postDescCell) continue;
        postingDate = dmyDash(postDescCell.s.slice(0, 10));
        desc = postDescCell.s.slice(11).trim();
      }
      if (!desc) continue;

      // Currency @x≈341
      const ccyCell = cells.find(c => c.x >= 335 && c.x <= 352 && /^[A-Z]{3}$/.test(c.s));

      // Original (foreign) amount @x≈382–400
      const origCell = cells.find(c => c.x >= 378 && c.x <= 405 && /^[\d,]+\.\d{2}$/.test(c.s));

      // Debit AED @x≈450–475 — present on expense rows
      const debitCell = cells.find(c => c.x >= 450 && c.x <= 475 && /^[\d,]+\.\d{2}$/.test(c.s));

      // Credit AED @x≈524–542 — present on payment/cashback rows
      const creditCell = cells.find(c => c.x >= 524 && c.x <= 542 && /^[\d,]+\.\d{2}$/.test(c.s));

      if (!debitCell && !creditCell) continue;

      const credit = !debitCell && !!creditCell;
      const amount = toNum(credit ? creditCell.s : debitCell.s);
      if (amount == null) continue;

      // The Debit/Credit column is ALWAYS the AED billed amount; the foreign side
      // (currency + original amount) is metadata. Store AED as `currency`, foreign
      // as originalCurrency/originalAmount (else new foreign imports get mislabeled).
      const fxCcy          = (ccyCell && ccyCell.s !== 'AED') ? ccyCell.s : null;
      const currency       = 'AED';
      const originalCurrency = fxCcy;
      const originalAmount = (fxCcy && origCell) ? toNum(origCell.s) : null;

      const tag = /FAB\s+REWARDS.*CASHBACK|CASHBACK.*REDEMPTION/i.test(desc) ? 'cashback'
        : /PAYMENT\s+RECEIVED|RECEIVED.*THANK/i.test(desc) ? 'payment'
        : /VAT\s+ON\s+SERVICE|MEMBERSHIP\s+FEE/i.test(desc) ? 'fee'
        : null;

      txns.push({
        date: trxnDate,
        postingDate,
        description: desc,
        amount,
        credit,
        currency,
        originalCurrency,
        originalAmount,
        tag,
      });
    }
  }

  return { bank: 'FAB', summary, transactions: txns };
}
